import { createSelector } from 'reselect';
import { selectors as concurrencySelectors } from '@saucelabs/sl-module-concurrency';
import { constants as userConstants, OrgPlansNotFetchedError } from '@saucelabs/sl-module-user';
import { selectors as tunnelSelectors } from '@saucelabs/sl-module-tunnels';
import { List, Set } from 'immutable';

import { selectors as testConfigurationSelectors } from '../../ducks/testConfiguration';
import commonUtils from '../sessionCommon/utils';
import {
  CONFIGURATION_TEST_TYPES,
} from '../../constants';
import constants from './constants';
import utils from './utils';
import groupUtils from './groupUtils';
import sortUtils from './sortUtils';

const {
  DEVICE_SORT_MODES,
  DEVICE_SUBSCRIPTION_VARS,
  MOBILE_SELECTION_FILTER,
  MOBILE_SELECTION_FILTER_CATEGORY,
  SORT_CATEGORIES,
  BRANDS,
} = constants;

const pinnedDeviceCompositeIdsLoadedSelector = (store) => (
  store.apps.manual.realDeviceSelection.get('pinnedDeviceCompositeIdsLoaded')
);
const starDevicesCompositeIdsSelector = (store) => (
  store.apps.manual.realDeviceSelection.get('starDevicesCompositeIds')
);
const unsortedDevicesSelector = (store) => (
  store.apps.manual.realDeviceSelection.get('items')
);
const activeFilterSelector = (store) => (
  store.apps.manual.realDeviceSelection.get('activeFilters')
);
const availableDeviceIdsSelector = (store) => (
  store.apps.manual.realDeviceSelection.get('availableDeviceIds')
);
const availableDeviceIdsLoadedSelector = (store) => (
  store.apps.manual.realDeviceSelection.get('availableDeviceIdsLoaded')
);
const devicesStatusSelector = (store) => (
  store.apps.manual.realDeviceSelection.get('devicesStatus')
);
const isDevicesStatusLoadedSelector = (store) => (
  store.apps.manual.realDeviceSelection.get('isDevicesStatusLoaded')
);
const sessionAvailableSelector = (store) => (
  store.apps.manual.realDeviceSelection.get('sessionAvailable')
);
const sortModeSelector = (store) => (
  store.apps.manual.realDeviceSelection.get('sortMode')
);

/**
 * A redux selector that returns an array of the device IDs that are currently available for tests.
 * This is needed during the web-ui-new-rdc-device-status experiment.
 */
const availableDevicesSelector = createSelector([
  availableDeviceIdsSelector,
  devicesStatusSelector,
], (
  availableDeviceIds,
  devicesStatus,
) => {
  if (availableDeviceIds.length > 0) {
    return availableDeviceIds;
  }
  if (devicesStatus.length > 0) {
    return devicesStatus.reduce((accumulator, currentValue) => {
      if (currentValue.state === constants.DEVICE_STATE.AVAILABLE) {
        accumulator.push(currentValue.descriptor);
      }
      return accumulator;
    }, []);
  }
  return [];
});

/**
 * A redux selector that returns a boolean stating if device availability has been fetched.
 * This is needed during the web-ui-new-rdc-device-status experiment.
 */
const hasAvailableDevicesBeenLoadedSelector = createSelector([
  availableDeviceIdsLoadedSelector,
  isDevicesStatusLoadedSelector,
], (
  availableDeviceIdsLoaded,
  isDevicesStatusLoaded,
) => (availableDeviceIdsLoaded || isDevicesStatusLoaded));

/**
 * A redux selector that returns a boolean which states if the current user is a free trial user
 * @param {Object} store - the redux store
 * @return {boolean} the boolean that represents if the user is currently a free trial user
 */
const isUserFreeTrialSelector = (store) => (
  store.user.getOrgUserType() === userConstants.USER_TYPE_FREE_TRIAL
);

/**
 * A redux selector that returns a boolean which states if the current user is an active free user
 * (either free trial or freemium)
 * @param {Object} store - the redux store
 * @return {boolean} the boolean that represents if the user is currently free (trial or freemium)
 */
// WARNING: This selector (and selectors including this selectors) may only be used in containers
// that fetch account's plans information (via getOrgPlans or loggedInUserWithOrgPlans). If neither
// is called before the selector is applied, it'll always return false.
const activeFreeUserSelector = ({ user }) => {
  try {
    return commonUtils.isActiveFreeUser(user);
  } catch (error) {
    if (error instanceof OrgPlansNotFetchedError) {
      return false;
    }
    throw error;
  }
};

/**
 * A redux selector that returns a boolean which states if the current user
 * is in an expired state (expired trial or expired subscription)
 * @param {Object} store - the redux store
 * @return {boolean} the boolean that represents if the user is currently an expired user
 */
const expiredUserSelector = (store) => {
  const userType = store.user.getOrgUserType();
  return userType === userConstants.USER_TYPE_FREE;
};

const filteredDevicesSelector = createSelector([
  (store) => store.apps.manual.realDeviceSelection.get('searchTerm'),
  activeFilterSelector,
  unsortedDevicesSelector,
  testConfigurationSelectors.mobileAppConfigurationSelector,
  starDevicesCompositeIdsSelector,

], (
  searchTerm,
  activeFilters,
  unsortedDevices,
  mobileAppConfiguration,
  pinnedDeviceIds,
) => {
  const osFilters = activeFilters.get('os');
  const osVersionFilters = activeFilters.get('osVersion');
  const brandFilters = activeFilters.get('manufacturer'); // I'm a Map!
  const resolutionFilters = activeFilters.get('resolution');
  const formFactorFilters = activeFilters.get('formFactor');
  const showPrivateDevices = activeFilters.get('cloudType').has('PRIVATE');
  const showCarrierConnectivity = activeFilters.get('networkType').has('CARRIER');
  const compatibilityFilter = activeFilters.get(MOBILE_SELECTION_FILTER_CATEGORY.COMPATIBILITY);
  const filteredDevices = unsortedDevices.filter((device) => {
    if (!(
      new RegExp(searchTerm, 'i').test(device.name) ||
        new RegExp(searchTerm, 'i').test(device.descriptorId))
    ) {
      return false;
    }

    if (
      compatibilityFilter.size > 0 &&
      mobileAppConfiguration &&
      !utils.isAppStorageFileCompatibleWithRealDeviceDescriptor(
        mobileAppConfiguration,
        device,
      )
    ) {
      return false;
    }

    if (osFilters.size > 0 && !(osFilters.has(device.os))) {
      return false;
    }
    if (osVersionFilters.size > 0 && !(osVersionFilters.has(device.osVersion))) {
      return false;
    }
    if (
      brandFilters.size > 0 &&
      !device.manufacturers.some((brand) => brandFilters.includes(brand))
    ) {
      return false;
    }
    if (resolutionFilters.size > 0 && !resolutionFilters.has(`${device.resolutionWidth}x${device.resolutionHeight}`)) {
      return false;
    }
    if (formFactorFilters.size > 0 && !(formFactorFilters.has(device.formFactor))) {
      return false;
    }
    if (showPrivateDevices && !device.isPrivateDevice()) {
      return false;
    }
    if (showCarrierConnectivity && !device.connectivity.includes('CARRIER')) {
      return false;
    }
    return true;
  });
  return groupUtils.groupByCategory(
    filteredDevices.toArray(), pinnedDeviceIds.toArray(),
  );
});

// WARNING: May only be used in containers that fetch account plans (because activeFreeUserSelector)
const sortedDevicesSelector = createSelector([
  activeFreeUserSelector,
  filteredDevicesSelector,
  sortModeSelector,
  availableDevicesSelector,
  starDevicesCompositeIdsSelector,
], (
  activeFreeUser,
  groupedDevices,
  sortMode,
  availableDeviceIds,
  pinnedDeviceIds,
) => {
  const { FREE, OTHER, PHONES, PINNED, PRIVATE } = SORT_CATEGORIES;

  const paidMergedIos17Devices = commonUtils.selectAvailableIos17devices(
    groupedDevices[BRANDS.IOS_17_PAID], availableDeviceIds);
  const freeMergedIos17Devices = commonUtils.selectAvailableIos17devices(
    groupedDevices[BRANDS.IOS_17_FREE], availableDeviceIds);

  const freeDevices = activeFreeUser ? groupedDevices[FREE] : [];
  // default aka DEVICE_SORT_MODES.PREFERRED_FIRST
  let paidDevices = [
    ...groupedDevices[PINNED],
    ...groupedDevices[PRIVATE],
    ...paidMergedIos17Devices,
    ...groupedDevices[PHONES],
    ...groupedDevices[OTHER],
  ];

  if (sortMode === DEVICE_SORT_MODES.PINNED_FIRST) {
    const unpinnedDevices = [
      ...groupedDevices[PRIVATE],
      ...groupedDevices[PHONES],
      ...groupedDevices[OTHER],
    ];
    paidDevices = [
      ...groupedDevices[PINNED],
      ...([...paidMergedIos17Devices, ...unpinnedDevices].sort(sortUtils.sortByName)).sort(
        (deviceA, deviceB) => sortUtils.sortByPinned(deviceA, deviceB, pinnedDeviceIds)),
    ];
  }

  if (sortMode === DEVICE_SORT_MODES.A_TO_Z) {
    paidDevices = paidDevices.sort(sortUtils.sortByName);
  }

  if (sortMode === DEVICE_SORT_MODES.Z_TO_A) {
    paidDevices = paidDevices.sort(sortUtils.sortByName).reverse();
  }

  return {
    [DEVICE_SUBSCRIPTION_VARS.FREE]: List([...freeDevices, ...freeMergedIos17Devices]),
    [DEVICE_SUBSCRIPTION_VARS.PAID]: List(paidDevices),
  };
});

// WARNING: This selector (and selectors including this selectors) may only be used in containers
// that fetch account's plans information (via getOrgPlans or loggedInUserWithOrgPlans). If neither
// is called before the selector is applied, it'll always return false.
const isManualMinutesExpiredSelector = createSelector([
  (store) => store.user,
  isUserFreeTrialSelector,
  (store) => store.usage?.usageUnits?.total_available_usage_units <= 0,
], (
  user,
  isOldFreeTrial,
  outOfUsageUnits,
) => {
  try {
    return (
      (isOldFreeTrial && user.get('manual_minutes') === 0) ||
      (user.isOnFreeTrialPlan() && outOfUsageUnits)
    );
  } catch (error) {
    if (error instanceof OrgPlansNotFetchedError) {
      return false;
    }
    throw error;
  }
});

// WARNING: May only be used in containers that fetch account plans (because activeFreeUserSelector
// and user.isFreemiumCapable)
const mobileSelectionSelector = createSelector([
  availableDevicesSelector,
  concurrencySelectors.concurrencySelector,
  (store) => store.apps.manual.realDeviceSelection.get('sessionAvailabilityLoaded'),
  sessionAvailableSelector,
  (store) => store.apps.manual.realDeviceSelection,
  starDevicesCompositeIdsSelector,
  (store) => store.user,
  tunnelSelectors.filteredUserTunnelsSelector,
  expiredUserSelector,
  sortedDevicesSelector,
  testConfigurationSelectors.realDeviceConfigurationSelector,
  testConfigurationSelectors.mobileAppConfigurationSelector,
  testConfigurationSelectors.testTypeSelector,
  testConfigurationSelectors.tunnelSelector,
  testConfigurationSelectors.isSessionLoadingSelector,
  activeFreeUserSelector,
  testConfigurationSelectors.urlSelector,
  isManualMinutesExpiredSelector,
],
(
  availableDeviceIds,
  concurrency,
  sessionAvailabilityLoaded,
  sessionAvailable,
  realDeviceSelection,
  starredRealDevices,
  user,
  userTunnels,
  expiredUser,
  sortedDevices,
  realDeviceConfiguration,
  mobileAppConfiguration,
  testType,
  tunnel,
  isSessionLoading,
  activeFreeUser,
  url,
  isManualMinutesExpired,
) => {
  const userSettings = user.get('organization').settings;
  const activeFilters = realDeviceSelection.get('activeFilters');
  let isFreemiumCapable = false;
  try {
    isFreemiumCapable = user.isFreemiumCapable();
  } catch (error) {
    if (!(error instanceof OrgPlansNotFetchedError)) {
      throw error;
    }
  }

  return {
    activeFilters,
    allowedUsersRdcConcurrency: concurrency.user.get('allowed')?.rdc,
    availableDeviceIds,
    currentFacets: realDeviceSelection.get('currentFacets'),
    currentView: realDeviceSelection.get('currentView'),
    devices: sortedDevices,
    email: user.get('email'),
    expiredUser,
    isFreemiumCapable,
    isFreemiumUser: user.isFreemiumUser(),
    isSessionLoading,
    isManualMinutesExpired,
    isOrgAdmin: user.isOrgAdmin(),
    // based on this thread: https://saucelabs.slack.com/archives/CA3ES45TR/p1633530438213600
    // rdc_enabled can be true with 0 devices
    isRdcEnabled: !!userSettings?.rdc_enabled && (realDeviceSelection.get('loading') || realDeviceSelection.get('items').size !== 0),
    isUnverifiedUser: !user.isVerified(),
    activeFreeUser,
    loading: realDeviceSelection.get('loading'),
    mobileAppConfiguration,
    realDeviceConfiguration,
    sauceConnectPublicRdcEnabled: userSettings?.can_use_tunnels_with_public_real_devices,
    searchTerm: realDeviceSelection.get('searchTerm'),
    sessionAvailabilityLoaded,
    sessionAvailable,
    sortMode: realDeviceSelection.get('sortMode'),
    starredRealDevices,
    testType,
    tunnel,
    tunnels: userTunnels,
    url,
  };
});

// WARNING: May only be used in containers that fetch account plans (because activeFreeUserSelector)
const vmdBannerSelector = createSelector([
  availableDevicesSelector,
  concurrencySelectors.concurrencySelector,
  hasAvailableDevicesBeenLoadedSelector,
  (store) => store.apps.manual.realDeviceSelection,
  sessionAvailableSelector,
  sortedDevicesSelector,
  testConfigurationSelectors.testTypeSelector,
  activeFreeUserSelector,
  (store) => store.user.get('organization').settings,
  (store) => store.user.getUserType(),
  (store) => store.usage?.usageUnits?.total_available_usage_units <= 0,
  (store) => store.usage?.usageUnits?.usage_unit,
  (store) => store.usage?.nextFreemiumReplenishment,
], (
  availableDeviceIds,
  concurrency,
  hasAvailableDevicesBeenLoaded,
  realDeviceSelection,
  sessionAvailable,
  sortedDevices,
  testType,
  activeFreeUser,
  userSettings,
  userType,
  outOfUsageUnits,
  usageUnit,
  creditRefreshDate,
) => {
  const sessionAvailabilityLoaded = realDeviceSelection.get('sessionAvailabilityLoaded');

  const searchTerm = realDeviceSelection.get('searchTerm');

  const activeFilters = realDeviceSelection.get('activeFilters');
  const filterIsSet = activeFilters.some((filter) => filter.size);

  const availableDevicesCount = sortedDevices.PAID.count((device) => (
    !utils.isDeviceDisabled(
      device, availableDeviceIds, sessionAvailable,
    )
  )) + sortedDevices.FREE.count((device) => (
    !utils.isDeviceDisabled(
      device, availableDeviceIds, sessionAvailable,
    )
  ));
  const allDevicesBusy = availableDevicesCount === 0;

  const isRdcEnabled = !!userSettings?.rdc_enabled && (
    realDeviceSelection.get('loading') || realDeviceSelection.get('items').size !== 0);

  const isAndroidOnly = activeFilters.get(
    MOBILE_SELECTION_FILTER_CATEGORY.OS).equals(Set([MOBILE_SELECTION_FILTER.ANDROID]));
  const isVmdFriendlyConfiguration = (
    testType === CONFIGURATION_TEST_TYPES.WEB ||
      (testType === CONFIGURATION_TEST_TYPES.APP && isAndroidOnly)
  );

  return {
    hasVmdConcurrencyAvailable: concurrency.user.get('allowed')?.vms > concurrency.user.get('current')?.vms,
    shouldTrackEventForActiveFreeUser: (
      isRdcEnabled &&
      allDevicesBusy &&
      !!sortedDevices.FREE.size &&
      !searchTerm &&
      activeFreeUser &&
      hasAvailableDevicesBeenLoaded &&
      sessionAvailabilityLoaded &&
      isVmdFriendlyConfiguration &&
      !filterIsSet
    ),
    shouldTrackEventForDeviceSearch: (
      isRdcEnabled &&
      allDevicesBusy &&
      !!sortedDevices.PAID.size &&
      !!searchTerm &&
      hasAvailableDevicesBeenLoaded &&
      isVmdFriendlyConfiguration &&
      sessionAvailabilityLoaded
    ),
    shouldTrackEventForDeviceFilter: (
      isRdcEnabled &&
      allDevicesBusy &&
      !!sortedDevices.PAID.size &&
      filterIsSet &&
      hasAvailableDevicesBeenLoaded &&
      isVmdFriendlyConfiguration &&
      sessionAvailabilityLoaded
    ),
    isBannerVisible: (
      isRdcEnabled &&
      allDevicesBusy &&
      !!sortedDevices.PAID.size &&
      hasAvailableDevicesBeenLoaded &&
      sessionAvailabilityLoaded
    ),
    isVmdFriendlyConfiguration,
    testType,
    userType,
    outOfUsageUnits,
    usageUnit,
    creditRefreshDate,
  };
});

export default {
  availableDevicesSelector,
  isManualMinutesExpiredSelector,
  mobileSelectionSelector,
  pinnedDeviceCompositeIdsLoadedSelector,
  sortModeSelector,
  starDevicesCompositeIdsSelector,
  vmdBannerSelector,
  sortedDevicesSelector,
};

export {
  activeFreeUserSelector,
  filteredDevicesSelector,
  starDevicesCompositeIdsSelector,
};
